<template>
  <div class="overflow-y-scroll w-full h-full px-5">
    <div class="mx-auto px-4 sm:px-8 xl:px-0 mt-6 flex gap-6">
      <aside class="w-1/5 hidden lg:block">
        <h3 class="text-lg font-semibold mb-4">Категории</h3>
        <ul class="space-y-2">
          <li
            v-for="cat in categories"
            :key="cat.id"
            @click="selectCategory(cat.id)"
            class="cursor-pointer text-sm px-3 py-2 rounded hover:bg-gray-100"
            :class="{ 'bg-gray-200 font-medium': selectedCategory === cat.id }"
          >
            {{ cat.name }}
          </li>
          <li
            @click="selectCategory(null)"
            class="cursor-pointer text-sm px-3 py-2 rounded hover:bg-gray-100"
            :class="{ 'bg-gray-200 font-medium': selectedCategory === null }"
          >
            Все категории
          </li>
        </ul>
      </aside>

      <div class="flex-1">
        <div class="max-w-[1170px] mx-auto px-4 sm:px-8 xl:px-0 mt-3 h-full">
          <div class="mb-12.5">
            <div class="flex justify-between items-center mb-6">
              <div class="text-center flex-1">
                <h1 class="text-dark text-3xl font-bold mb-3.5">Законы и нормативные документы</h1>
                <p class="text-gray-600 mb-3">Все документы доступны для просмотра и скачивания</p>
              </div>
              <button
                v-if="isAuthenticated && (user.role && user.role.key !== 1)"
                @click="showSuggestForm = true"
                class="px-4 md:px-6 py-2 bg-gray-800 text-white rounded-xl hover:bg-gray-700 transition font-medium text-xs md:text-sm whitespace-nowrap ml-4"
              >
                <b>Предложить закон</b>
              </button>
            </div>
          </div>

          <div v-if="lawList === null" class="text-center text-gray-500 mt-6">Загрузка...</div>
          <div v-else-if="lawList.length === 0" class="text-center text-gray-500 mt-6">Нет данных.</div>

          <div v-else class="space-y-8">
            <div
              v-for="law in lawList"
              :key="law.slug"
              class="border rounded-md p-6 shadow-sm hover:shadow-md transition-all duration-300"
            >
              <h2 class="text-xl font-semibold mb-2">{{ law.title }}</h2>
              <p class="text-gray-700 mb-4" v-html="law.content"></p>

              <div v-if="law.file_attachment">
                <a
                  :href="law.file_attachment"
                  target="_blank"
                  class="inline-flex items-center text-blue-600 hover:underline text-sm"
                >
                  📎 {{ getFileName(law.file_attachment) }}
                </a>
              </div>
            </div>
          </div>

          <!-- Pagination -->
          <div v-if="totalPages > 1" class="flex justify-center items-center mt-10 gap-2 flex-wrap">
            <label class="mr-2 text-sm font-medium">Страница:</label>
            <select
              v-model.number="currentPage"
              @change="handlePageChange"
              class="border px-3 py-2 rounded-md w-20"
            >
              <option v-for="n in totalPages" :key="n" :value="n">
                {{ n }}
              </option>
            </select>
            <span class="text-sm text-gray-600">из {{ totalPages }}</span>
          </div>
        </div>
        <div class="pb-3"></div>
      </div>
    </div>

    <!-- Modal: Suggest Content -->
    <div v-if="showSuggestForm" class="fixed inset-0 bg-black bg-opacity-50 z-[10010] flex items-center justify-center p-4">
      <div class="bg-white rounded-lg shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div class="sticky top-0 bg-white border-b p-6 flex justify-between items-center">
          <h2 class="text-2xl font-bold text-gray-900">Предложить закон</h2>
          <button
            @click="showSuggestForm = false"
            class="text-gray-500 hover:text-gray-700 text-2xl"
          >
            ✕
          </button>
        </div>

        <div class="p-6">
          <ContentSuggestForm
            content-type="law"
            @submit="handleSuggestSubmit"
            @close="showSuggestForm = false"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from "axios";
import { API_CONTENT, API_CATEGORIES_CLIENT } from "@/const/uri";
import ContentSuggestForm from "@/components/PageContent/ContentSuggestForm.vue";
import { mapState } from "vuex";

export default {
  name: "PageLaws",
  components: {
    ContentSuggestForm,
  },
  data() {
    return {
      lawList: null,
      currentPage: 1,
      perPage: 10,
      totalPages: 1,
      showSuggestForm: false,
      isAuthenticated: false,
      categories: [],
      selectedCategory: null,
    };
  },
  computed: {
    ...mapState(["user"]),
    queryPage() {
      const p = parseInt(this.$route.query.p);
      return isNaN(p) || p < 1 ? 1 : p;
    },
  },
  watch: {
    "$route.query.p": {
      immediate: true,
      handler() {
        this.currentPage = this.queryPage;
        this.fetchLaws();
      },
    },
  },
  methods: {
    async fetchCategories() {
      try {
        const res = await axios.get(API_CATEGORIES_CLIENT.base, {
          params: {
            content_type: this.$route.params.contentType,
          },
        });
        if (res.data.code === 0) {
          this.categories = res.data.data;
        }
      } catch (error) {
        console.error("Ошибка загрузки категорий:", error);
      }
    },
    selectCategory(categoryId) {
      this.selectedCategory = categoryId;
      this.currentPage = 1;
      this.updateRoutePage(1);
      this.fetchLaws();
    },
    updateRoutePage(page) {
      this.$router.push({
        path: `/page/${this.$route.params.contentType}`,
        query: {
          ...this.$route.query,
          p: page,
        },
      });
    },
    async fetchLaws() {
      try {
        const response = await axios.get(API_CONTENT.list, {
          params: {
            content_type: this.$route.params.contentType,
            p: this.currentPage,
            category: this.selectedCategory || undefined,
            page_size: this.perPage,
          },
        });

        if (response.data.code === 0) {
          const data = response.data.data;
          this.lawList = data.results;
          this.totalPages = Math.ceil(data.count / this.perPage);
        }
      } catch (error) {
        console.error("Ошибка при загрузке законов:", error);
      }
    },
    handlePageChange() {
      this.updateRoutePage(this.currentPage);
    },
    getFileName(url) {
      return decodeURIComponent(url.split("/").pop());
    },
    checkAuthentication() {
      const token = localStorage.getItem('user_token');
      this.isAuthenticated = !!token;
    },
    handleSuggestSubmit() {
      this.showSuggestForm = false;
      this.fetchLaws();
    },
  },
  mounted() {
    this.checkAuthentication();
    this.fetchCategories();
  },
};
</script>
